import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Stack,
    Divider,
    Grid,
} from '@mui/material';
import { Vehicle } from '../../stores/VehicleStore';

interface VehicleDetailViewProps {
    vehicle: Vehicle;
}

const VehicleDetailView: React.FC<VehicleDetailViewProps> = ({ vehicle }) => {

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ACTIVE':
                return 'success';
            case 'MAINTENANCE':
                return 'warning';
            case 'INACTIVE':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'ACTIVE':
                return 'Activo';
            case 'MAINTENANCE':
                return 'En Mantenimiento';
            case 'INACTIVE':
                return 'Inactivo';
            default:
                return status;
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'No disponible';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Fecha inválida';
            }
            return date.toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        } catch (error) {
            return 'Error al formatear fecha';
        }
    };

    const safeValue = (value: any) => {
        if (value === null || value === undefined || value === '') {
            return 'No disponible';
        }
        return String(value);
    };

    const DetailItem: React.FC<{ label: string; value: string | number; mono?: boolean }> = ({ 
        label, 
        value, 
        mono = false 
    }) => (
        <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
                {label}
            </Typography>
            <Typography 
                variant="body1" 
                fontWeight={500}
                sx={{ fontFamily: mono ? 'monospace' : 'inherit' }}
            >
                {value}
            </Typography>
        </Box>
    );

    return (
        <Box>
            <Stack spacing={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight="600">
                        {vehicle.license_plate}
                    </Typography>
                    <Chip
                        label={getStatusLabel(vehicle.status)}
                        color={getStatusColor(vehicle.status) as any}
                        size="medium"
                        sx={{ fontWeight: 500 }}
                    />
                </Box>

                <Divider />

                <Box>
                    <Typography variant="h6" fontWeight="600" mb={2}>
                        Información General
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <DetailItem label="Marca" value={safeValue(vehicle.brand)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <DetailItem label="Modelo" value={safeValue(vehicle.model)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <DetailItem label="Año" value={safeValue(vehicle.year)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <DetailItem label="Color" value={safeValue(vehicle.color)} />
                        </Grid>
                    </Grid>
                </Box>

                <Divider />
                <Box>
                    <Typography variant="h6" fontWeight="600" mb={2}>
                        Identificadores
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12 }}>
                            <DetailItem 
                                label="VIN (Número de Identificación del Vehículo)" 
                                value={safeValue(vehicle.vin)} 
                                mono 
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DetailItem 
                                label="Número de Motor" 
                                value={safeValue(vehicle.engine_number)} 
                                mono 
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <DetailItem 
                                label="Número de AirTag" 
                                value={safeValue(vehicle.airtag_number)} 
                                mono 
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider />
                <Box>
                    <Typography variant="h6" fontWeight="600" mb={2}>
                        Registro y Fechas
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <DetailItem 
                                label="Fecha de Adquisición" 
                                value={formatDate(vehicle.acquisition_date)} 
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <DetailItem 
                                label="Registrado en Sistema" 
                                value={formatDate(vehicle.created_at)} 
                            />
                        </Grid>
                        {vehicle.updated_at && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <DetailItem 
                                    label="Última Actualización" 
                                    value={formatDate(vehicle.updated_at)} 
                                />
                            </Grid>
                        )}
                    </Grid>
                </Box>
                {(vehicle.current_location_lat || vehicle.current_location_lng) && (
                    <>
                        <Divider />
                        <Box>
                            <Typography variant="h6" fontWeight="600" mb={2}>
                                Ubicación Actual
                            </Typography>
                            <Grid container spacing={3}>
                                {vehicle.current_location_lat && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <DetailItem 
                                            label="Latitud" 
                                            value={safeValue(vehicle.current_location_lat)} 
                                            mono 
                                        />
                                    </Grid>
                                )}
                                {vehicle.current_location_lng && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <DetailItem 
                                            label="Longitud" 
                                            value={safeValue(vehicle.current_location_lng)} 
                                            mono 
                                        />
                                    </Grid>
                                )}
                                {vehicle.last_location_update && (
                                    <Grid size={{ xs: 12 }}>
                                        <DetailItem 
                                            label="Última Actualización de Ubicación" 
                                            value={formatDate(vehicle.last_location_update)} 
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </>
                )}
                {vehicle.image_url && (
                    <>
                        <Divider />
                        <Box>
                            <Typography variant="h6" fontWeight="600" mb={2}>
                                Imagen del Vehículo
                            </Typography>
                            <Box
                                component="img"
                                src={vehicle.image_url}
                                alt={`${safeValue(vehicle.brand)} ${safeValue(vehicle.model)}`}
                                sx={{
                                    maxWidth: '100%',
                                    maxHeight: 300,
                                    borderRadius: 2,
                                    boxShadow: 2
                                }}
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                }}
                            />
                        </Box>
                    </>
                )}
            </Stack>
        </Box>
    );
};

export default VehicleDetailView;
